import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from "react"
import { cn } from "@/lib/utils"

export interface BottomSheetProps {
  /** Show or hide the sheet. When `false` the component returns `null`. */
  open: boolean
  /** Called when the sheet should close (backdrop tap, escape, drag-down-from-sheet). */
  onClose: () => void
  /** Controlled expanded state. Pass with `onExpandedChange` for external control. */
  expanded?: boolean
  /** Initial expanded state for uncontrolled usage. Defaults to `false` (sheet at 60vh). */
  defaultExpanded?: boolean
  /** Fires whenever the expanded state changes (drag, tap, escape). */
  onExpandedChange?: (next: boolean) => void
  /** Body content rendered below the drag handle. */
  children: ReactNode
  /** Accessibility label for the dialog. */
  ariaLabel?: string
  /** Whether pressing Escape collapses (when expanded) or closes the sheet. */
  closeOnEscape?: boolean
  /** Pixel distance the handle must move before a drag is treated as an intentional swipe. */
  dragThresholdPx?: number
  /** Extra classes on the sheet container. */
  className?: string
}

const DRAG_THRESHOLD_DEFAULT = 40
const EXIT_DURATION_MS = 300
const EXIT_FALLBACK_MS = EXIT_DURATION_MS + 60

export function BottomSheet({
  open,
  onClose,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onExpandedChange,
  children,
  ariaLabel,
  closeOnEscape = true,
  dragThresholdPx = DRAG_THRESHOLD_DEFAULT,
  className,
}: BottomSheetProps) {
  const dragRef = useRef({ active: false, startY: 0 })
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isClosing, setIsClosing] = useState(false)

  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const isExpandedControlled = controlledExpanded !== undefined
  const isExpanded = isExpandedControlled ? controlledExpanded : internalExpanded

  // Reset internal state across open transitions using the React-documented
  // "store information from previous renders" pattern, so we don't need a
  // setState-in-effect to mirror prop changes.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (!open) {
      if (isClosing) setIsClosing(false)
    } else if (!isExpandedControlled) {
      if (internalExpanded !== defaultExpanded) {
        setInternalExpanded(defaultExpanded)
      }
    }
  }

  const setExpanded = useCallback(
    (next: boolean) => {
      if (!isExpandedControlled) setInternalExpanded(next)
      onExpandedChange?.(next)
    },
    [isExpandedControlled, onExpandedChange],
  )

  const finishClose = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current)
      exitTimerRef.current = null
    }
    onClose()
  }, [onClose])

  const beginClose = useCallback(() => {
    if (isClosing) return
    setIsClosing(true)
    exitTimerRef.current = setTimeout(finishClose, EXIT_FALLBACK_MS)
  }, [isClosing, finishClose])

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!closeOnEscape || !open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      if (isClosing) return
      if (isExpanded) setExpanded(false)
      else beginClose()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, isExpanded, isClosing, closeOnEscape, beginClose, setExpanded])

  if (!open) return null

  const onSheetTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (!isClosing) return
    if (e.target !== e.currentTarget) return
    if (e.propertyName !== "transform") return
    finishClose()
  }

  const onHandleDown = (e: PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    dragRef.current = { active: true, startY: e.clientY }
  }
  const onHandleUp = (e: PointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current.active) return
    const dy = e.clientY - dragRef.current.startY
    dragRef.current.active = false
    e.currentTarget.releasePointerCapture?.(e.pointerId)
    if (isClosing) return
    if (dy < -dragThresholdPx) {
      if (!isExpanded) setExpanded(true)
    } else if (dy > dragThresholdPx) {
      if (isExpanded) setExpanded(false)
      else beginClose()
    } else {
      setExpanded(!isExpanded)
    }
  }

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 motion-reduce:transition-none",
          isClosing && "opacity-0",
        )}
        onClick={beginClose}
        aria-hidden="true"
      />
      <div
        onTransitionEnd={onSheetTransitionEnd}
        style={{
          transition: `transform ${EXIT_DURATION_MS}ms ease-out, height 200ms ease-out`,
          transform: isClosing ? "translateY(100%)" : "translateY(0)",
          willChange: "transform",
        }}
        className={cn(
          "fixed bottom-0 right-0 z-50 w-full max-w-[680px] bg-background border border-border rounded-t-xl",
          "flex flex-col overflow-hidden shadow-lg",
          isExpanded ? "h-[100dvh]" : "h-[60vh]",
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        <button
          type="button"
          onPointerDown={onHandleDown}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
          aria-label={
            isExpanded
              ? "Drag to resize, tap to collapse"
              : "Drag to resize, tap to expand"
          }
          className="shrink-0 w-full pt-2 pb-2 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none group"
        >
          <span className="w-9 h-1 rounded-full bg-muted-foreground/40 group-hover:bg-muted-foreground/60 group-active:bg-muted-foreground/80 transition-colors" />
        </button>

        <div className="flex-1 min-h-0">{children}</div>
      </div>
    </>
  )
}
